/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useRef, useState, useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import ShortcutsIcon from '@atlaskit/icon/core/link';
import { xcss, Box } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import { ShortcutsContainer } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { useNavigationItemAnalytics } from '@atlassian/jira-horizontal-nav-common/src/common/analytics.tsx';
import { useTabContext } from '@atlassian/navigation-kit-ui-tab';
import { useShortcutsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/shortcuts-data/index.tsx';
import { useProjectIdOrKey } from '@atlassian/jira-horizontal-nav-common/src/common/utils.tsx';
import { TabWithMenu } from '../TabWithMenu.tsx';
import type { ShortcutsTab_tab$key } from './__generated__/ShortcutsTab_tab.graphql';
import type { ShortcutsTab_container$key } from './__generated__/ShortcutsTab_container.graphql';
import { shortcutMenuContentEntryPoint } from './content/entrypoint.tsx';

export const ShortcutsTab = ({
	tabFragment,
	containerFragment,
	beforeItemId,
	afterItemId,
	position,
	isRenaming,
	onRenamingChange,
}: {
	tabFragment: ShortcutsTab_tab$key;
	containerFragment: ShortcutsTab_container$key;
	beforeItemId: string | null;
	afterItemId: string | null;
	position: number;
	isRenaming: boolean;
	onRenamingChange: (isRenaming: boolean) => void;
}): React.JSX.Element => {
	const cloudId = useCloudId();
	const projectIdOrKey = useProjectIdOrKey() ?? '';
	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);
	const closePopup = useCallback(() => setIsDropdownMenuOpen(false), []);
	const itemRef = useRef<HTMLElement | null>(null);
	const { data: shortcutsData, loading, refresh } = useShortcutsData();
	const { isOverflowing } = useTabContext();

	const tabData = useFragment(
		graphql`
			fragment ShortcutsTab_tab on JiraNavigationItem {
				...TabWithMenu_tab
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment ShortcutsTab_container on JiraContainerNavigationResult {
				...TabWithMenu_container
				... on JiraContainerNavigation {
					canEdit @required(action: THROW)
				}
			}
		`,
		containerFragment,
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		shortcutMenuContentEntryPoint,
		useMemo(
			() => ({
				cloudId,
				projectIdOrKey,
			}),
			[cloudId, projectIdOrKey],
		),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isDropdownMenuOpen);

	const runtimeProps = useMemo(
		() => ({ canEdit: containerData.canEdit ?? false, onClosePopup: closePopup, itemRef }),
		[containerData.canEdit, closePopup],
	);

	const triggerAnalytics = useNavigationItemAnalytics(
		useMemo(
			() => ({
				itemId: undefined,
				itemType: 'shortcuts',
				navState: V4,
				stage: getNav4Rollout().stage,
				navigationLayer: CONTAINER,
				navigationContainer: TAB,
				isInMore: false,
			}),
			[],
		),
	);

	const trigger = ({ ref, ...triggerProps }: TriggerProps) => (
		<TabWithMenu
			tabFragment={tabData}
			containerFragment={containerData}
			type="dropdown-trigger"
			isOpen={isDropdownMenuOpen}
			isRenaming={isRenaming}
			onRenamingChange={onRenamingChange}
			elemBefore={<ShortcutsIcon label="" />}
			onClick={() => {
				setIsDropdownMenuOpen((isOpen) => !isOpen);
				triggerAnalytics();
			}}
			position={position}
			ref={(element) => {
				itemRef.current = element;
				triggerRef(element);
				typeof ref === 'function' && ref(element);
			}}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			testId="horizontal-nav-shortcuts-tab.dropdown-menu-trigger"
			{...triggerProps}
		/>
	);

	return (
		<Popup
			trigger={trigger}
			placement={isOverflowing ? 'left' : 'bottom-end'}
			shouldFlip={false}
			isOpen={isDropdownMenuOpen}
			onClose={closePopup}
			testId="horizontal-nav-shortcuts-tab.dropdown-menu"
			content={() => (
				<ShortcutsContainer
					onRefresh={fg('jira_refresh_shortcuts_on_change') ? refresh : undefined}
					data={shortcutsData}
					loading={loading}
					isGlobal
				>
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="shortcut.shortcuts-menu-content"
						packageName="jiraHorizontalNavShortcutsTab"
						runtimeProps={runtimeProps}
						fallback={<Box xcss={fallbackStyles} />}
					/>
				</ShortcutsContainer>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-navigation')}
		/>
	);
};

const fallbackStyles = xcss({ width: '360px', height: '200px' });
